import React, { useState, useEffect, useRef, useCallback } from 'react';

const AppSection = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [videosLoaded, setVideosLoaded] = useState({});
    const [videoErrors, setVideoErrors] = useState({});
    const sectionRef = useRef(null);
    const videoRefs = useRef([]);

    const contentData = [
        {
            heading: "Browse & Bid on Loads",
            subheading: "Check all available loads with complete details and bid at your competitive price. Monitor your real-time bidding position against other carriers.",
            description: "Access a comprehensive marketplace of freight opportunities tailored for carriers. View detailed load specifications, pickup and delivery locations, cargo requirements, and preferred timelines. Submit competitive bids instantly and track your position in real-time bidding processes. Our intelligent matching system connects you with shippers looking for reliable transportation partners, helping you maximize your revenue potential.",
            features: ["Live load marketplace", "Competitive bidding system", "Real-time bid tracking", "Detailed cargo specifications"],
            highlight: "Smart Bidding!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/carrieranimation1.mp4",
            fallbackIcon: "💰"
        },
        {
            heading: "Fleet Management Hub",
            subheading: "Monitor all your trucks with real-time status updates, add new vehicles, and manage your entire fleet efficiently. Update truck locations instantly to receive relevant load opportunities.",
            description: "Take complete control of your transportation fleet with our advanced management dashboard. Add and configure new trucks, monitor vehicle status across your entire operation, and keep real-time location data updated to maximize load opportunities. Our system automatically matches nearby available trucks with suitable loads, ensuring optimal utilization and reduced empty miles for your fleet.",
            features: ["Real-time fleet tracking", "Vehicle status monitoring", "Easy truck registration", "Location-based load matching"],
            highlight: "Fleet Control!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/carrieranimation2.mp4",
            fallbackIcon: "🚛"
        },
        {
            heading: "Trip Status Dashboard",
            subheading: "Monitor all trips with comprehensive real-time status updates including vehicle assignment, document uploads, POD submissions, and payment tracking.",
            description: "Stay on top of every aspect of your transportation operations with our comprehensive trip management system. Track vehicle assignments, monitor document upload requirements, manage proof of delivery submissions, and keep tabs on payment status for all active trips. Receive instant notifications for pending actions, document requirements, or payment updates, ensuring smooth operations and faster cash flow.",
            features: ["Complete trip visibility", "Document management", "POD tracking system", "Payment status monitoring"],
            highlight: "Full Visibility!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/carrieranimation3.mp4",
            fallbackIcon: "📋"
        },
        {
            heading: "Complete Trip Management",
            subheading: "Access comprehensive trip details including real-time status, payment updates, direct driver communication, and live driver location tracking for complete peace of mind.",
            description: "Experience unparalleled control over your transportation operations with our all-in-one trip management platform. Monitor payment processing, access detailed trip histories, maintain direct communication channels with drivers, and track vehicle locations in real-time. Generate comprehensive reports for business analysis, manage multiple concurrent trips, and maintain complete operational visibility from a single, intuitive dashboard designed specifically for carriers.",
            features: ["Centralized trip control", "Driver communication", "Payment processing", "Comprehensive reporting"],
            highlight: "Total Control!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/carrieranimation4.mp4",
            fallbackIcon: "📊"
        },
        {
            heading: "AI-Powered Route Optimization",
            subheading: "Get real-time vehicle location with AI-based traffic analysis, optimal route suggestions, and weather updates for the most efficient and safe transportation solutions.",
            description: "Leverage cutting-edge artificial intelligence to optimize your transportation routes and reduce operational costs. Our AI algorithms analyze real-time traffic patterns, weather conditions, fuel prices, and road conditions to suggest the most efficient routes for your drivers. Minimize fuel consumption, reduce delivery times, and improve driver safety with intelligent routing that adapts to changing conditions throughout the journey.",
            features: ["AI route optimization", "Real-time traffic analysis", "Weather integration", "Fuel cost optimization"],
            highlight: "Smart Routes!",
            videoSrc: "https://prod-roadexpress.s3.ap-south-1.amazonaws.com/website/carrieranimation5.mp4",
            fallbackIcon: "🗺️"
        }
    ];

    // Calculate current video index and transition progress for smooth fading
    const getVideoTransition = useCallback(() => {
        const totalSections = contentData.length;
        const sectionProgress = scrollProgress * totalSections;
        const currentVideoIndex = Math.min(Math.floor(sectionProgress), totalSections - 1);
        const nextVideoIndex = Math.min(currentVideoIndex + 1, totalSections - 1);

        // Calculate fade progress within current section
        const withinSectionProgress = sectionProgress - currentVideoIndex;
        const fadeThreshold = 0.8; // Start fading at 80% through section

        let fadeProgress = 0;
        if (withinSectionProgress > fadeThreshold && currentVideoIndex < totalSections - 1) {
            fadeProgress = (withinSectionProgress - fadeThreshold) / (1 - fadeThreshold);
        }

        return {
            currentIndex: currentVideoIndex,
            nextIndex: nextVideoIndex,
            fadeProgress: Math.max(0, Math.min(1, fadeProgress))
        };
    }, [scrollProgress, contentData.length]);

    const { currentIndex, nextIndex, fadeProgress } = getVideoTransition();

    // Enhanced smooth scroll handling with continuous progress tracking
    useEffect(() => {
        let ticking = false;
        let lastScrollTime = 0;

        const handleScroll = () => {
            const now = Date.now();

            if (!ticking && now - lastScrollTime > 8) { // ~120fps throttling for smoother response
                requestAnimationFrame(() => {
                    if (!sectionRef.current) {
                        ticking = false;
                        return;
                    }

                    const sectionRect = sectionRef.current.getBoundingClientRect();
                    const sectionHeight = sectionRect.height;
                    const windowHeight = window.innerHeight;

                    // Calculate scroll progress with better boundaries
                    const startPoint = sectionRect.top;
                    const endPoint = sectionRect.bottom - windowHeight;
                    const totalScrollDistance = sectionHeight - windowHeight;

                    let progress = 0;
                    if (startPoint > 0) {
                        // Before the section starts
                        progress = 0;
                    } else if (endPoint < 0) {
                        // After the section ends
                        progress = 1;
                    } else {
                        // Within the section
                        progress = Math.abs(startPoint) / totalScrollDistance;
                        progress = Math.max(0, Math.min(1, progress));
                    }

                    // Update scroll progress for smooth text movement
                    setScrollProgress(progress);

                    ticking = false;
                    lastScrollTime = now;
                });
                ticking = true;
            }
        };

        // Use passive scrolling for better performance
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle video playback based on current index with smooth transitions
    useEffect(() => {
        videoRefs.current.forEach((video, index) => {
            if (video) {
                try {
                    // Play current video and next video during transition
                    if (index === currentIndex || (index === nextIndex && fadeProgress > 0)) {
                        if (video.paused) {
                            video.currentTime = 0;
                            video.play().catch(() => { });
                        }
                    } else {
                        if (!video.paused) {
                            video.pause();
                        }
                    }
                } catch (error) {
                    console.warn(`Video control error for index ${index}:`, error);
                }
            }
        });
    }, [currentIndex, nextIndex, fadeProgress]);

    const handleVideoLoad = useCallback((index) => {
        setVideosLoaded(prev => ({ ...prev, [index]: true }));

        // Auto-play first video when it loads
        if (index === 0 && videoRefs.current[0]) {
            videoRefs.current[0].play().catch(() => { });
        }
    }, []);

    const handleVideoError = useCallback((index) => {
        setVideoErrors(prev => ({ ...prev, [index]: true }));
        console.error(`Failed to load video ${index + 1}`);
    }, []);

    // Calculate video opacity for smooth transitions
    const getVideoOpacity = (index) => {
        if (index === currentIndex) {
            return fadeProgress > 0 ? 1 - fadeProgress : 1;
        } else if (index === nextIndex && fadeProgress > 0) {
            return fadeProgress;
        }
        return 0;
    };

    // Preload videos for smoother transitions
    useEffect(() => {
        contentData.forEach((content, index) => {
            if (videoRefs.current[index] && !videosLoaded[index] && !videoErrors[index]) {
                videoRefs.current[index].load();
            }
        });
    }, [videosLoaded, videoErrors, contentData]);

    // Calculate smooth text transform based on scroll progress
    const getTextTransform = () => {
        // Calculate the position for the sliding text container
        const totalSections = contentData.length;
        const sectionProgress = scrollProgress * (totalSections - 1);
        const translateY = sectionProgress * 100; // Use viewport height units
        return `translateY(-${translateY}vh)`;
    };

    return (
        <div className="relative w-full">
            {/* Extended scroll section - height matches number of sections */}
            <section
                ref={sectionRef}
                className="min-h-[500vh] bg-gray-50 relative w-full"
                aria-labelledby="carrier-app-section-title"
            >
                {/* Container positioned relative to scroll normally */}
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="sticky top-0 h-screen flex flex-col justify-center z-10 w-full">

                        {/* Main Content Grid */}
                        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center px-4 sm:px-6 lg:px-8 w-full">

                            {/* Text Content Section (Left) */}
                            <div className="relative h-screen overflow-hidden w-full order-1 lg:order-1">
                                {/* Sliding container with smooth continuous movement */}
                                <div
                                    className="will-change-transform"
                                    style={{
                                        transform: getTextTransform(),
                                        transition: 'none' // Remove transition for smooth scroll-based movement
                                    }}
                                >
                                    {/* Text content sections */}
                                    {contentData.map((content, index) => (
                                        <div
                                            key={index}
                                            className="h-screen flex flex-col justify-center space-y-6 px-4"
                                        >
                                            {/* Badge */}
                                            <div className="mb-4">
                                                <span className="inline-block bg-[#F05A28]/10 text-[#F05A28] px-4 py-2 rounded-full text-sm font-semibold">
                                                    {content.highlight}
                                                </span>
                                            </div>

                                            {/* Heading */}
                                            <div className="mb-6">
                                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight">
                                                    {content.heading.split(' ').map((word, wordIndex) => (
                                                        <span
                                                            key={`${index}-${wordIndex}`}
                                                            className={`inline-block mr-2 ${['Bid', 'Management', 'Status', 'Trip', 'Optimization', 'Browse'].some(highlight =>
                                                                word.includes(highlight)
                                                            ) ? 'text-[#F05A28]' : ''
                                                                }`}
                                                        >
                                                            {word}
                                                        </span>
                                                    ))}
                                                </h2>
                                            </div>

                                            {/* Extended Description */}
                                            <div className="mb-8">
                                                <p className="text-base text-gray-700 leading-relaxed">
                                                    {content.description}
                                                </p>
                                            </div>

                                            {/* Feature List */}
                                            <div className="mb-6">
                                                <div className="grid grid-cols-2 gap-3">
                                                    {content.features.map((feature, featureIndex) => (
                                                        <div key={featureIndex} className="flex items-center space-x-2">
                                                            <div className="w-2 h-2 bg-[#F05A28] rounded-full flex-shrink-0"></div>
                                                            <span className="text-sm text-gray-600 font-medium">{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Video Section (Right) - Full width, no constraints */}
                            <div className="relative w-full order-2 lg:order-2">
                                {/* Video container for landscape videos - adjusted aspect ratio */}
                                <div className="relative overflow-hidden rounded-2xl aspect-[8/10] max-w-2xl mx-auto w-full">
                                    {/* Video stack */}
                                    {contentData.map((content, index) => {
                                        const opacity = getVideoOpacity(index);

                                        return (
                                            <div
                                                key={index}
                                                className="absolute inset-0 h-full w-full"
                                                style={{
                                                    opacity: opacity,
                                                    zIndex: index === currentIndex || index === nextIndex ? 10 : 1,
                                                    transition: 'opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)'
                                                }}
                                            >
                                                {!videoErrors[index] ? (
                                                    <video
                                                        ref={el => {
                                                            if (el) videoRefs.current[index] = el;
                                                        }}
                                                        src={content.videoSrc}
                                                        className="w-full h-full object-cover"
                                                        onLoadedData={() => handleVideoLoad(index)}
                                                        onError={() => handleVideoError(index)}
                                                        loop
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        controls={false}
                                                        aria-label={`${content.heading} demonstration video`}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-orange-100">
                                                        <div className="text-center p-4">
                                                            <div className="text-4xl mb-2">{content.fallbackIcon}</div>
                                                            <div className="text-lg font-semibold text-gray-800">{content.heading}</div>
                                                            <div className="text-sm text-gray-600">Feature Preview</div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SEO structured data */}
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "MobileApplication",
                            "name": "RoadExpress Carrier Platform",
                            "description": "Professional carrier management platform with load bidding, fleet tracking, trip management, and AI-powered route optimization for transportation partners",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web, iOS, Android",
                            "offers": {
                                "@type": "Offer",
                                "price": "0",
                                "priceCurrency": "INR"
                            },
                            "author": {
                                "@type": "Organization",
                                "name": "RoadExpress"
                            }
                        })
                    }}
                />
            </section>
        </div>
    );
};

export default AppSection;